import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db, collections } from '../../firebase.marketplace';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSpinner, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';

// Import Tab Components
import TabNavigation from './TabNavigation';
import ProductsTab from './ProductsTab';
import EventsTab from './EventsTab';
import AnalyticsTab from './AnalyticsTab';

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  color: white;
`;

const Header = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  padding: 2.5rem;
  border-radius: 20px;
  margin-bottom: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(74, 144, 226, 0.1), transparent 70%);
    z-index: 0;
  }
`;

const Stats = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.5rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-color: rgba(74, 144, 226, 0.3);
  }
`;

const StatTitle = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
`;

const WithdrawBanner = styled(motion.div)`
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(74, 144, 226, 0.05) 100%);
  padding: 1.5rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(74, 144, 226, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
    button {
      margin-top: 1rem;
      align-self: flex-end;
    }
  }
`;

const ContentArea = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const ArtistDashboardNew = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for tab navigation
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // State for data
  const [earnings, setEarnings] = useState({
    total: 0,
    available: 0,
    pending: 0
  });
  const [withdrawals, setWithdrawals] = useState([]);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  
  // Fetch data when component mounts
  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);
  
  // Fetch data based on active tab
  useEffect(() => {
    if (currentUser) {
      if (activeTab === 'products') {
        fetchProducts();
      } else if (activeTab === 'events') {
        fetchEvents();
      } else if (activeTab === 'dashboard') {
        fetchEarnings();
        fetchWithdrawals();
      }
    }
  }, [currentUser, activeTab]);
  
  // Fetch initial data
  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProducts(),
        fetchEvents(),
        fetchEarnings(),
        fetchWithdrawals()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch products
  const fetchProducts = async () => {
    if (!currentUser) return;
    try {
      const productsRef = collection(db, collections.PRODUCTS);
      const q = query(productsRef, where('artistId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const productsData = [];
      querySnapshot.forEach((doc) => {
        productsData.push({
          $id: doc.id,
          ...doc.data()
        });
      });
      
      setProducts(productsData);
      return productsData;
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load your products');
      return [];
    }
  };
  
  // Fetch events
  const fetchEvents = async () => {
    if (!currentUser) return;
    try {
      const eventsRef = collection(db, collections.EVENTS);
      const q = query(eventsRef, where('artistId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const eventsData = [];
      querySnapshot.forEach((doc) => {
        eventsData.push({
          $id: doc.id,
          ...doc.data()
        });
      });
      
      setEvents(eventsData);
      return eventsData;
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load your events');
      return [];
    }
  };
  
  // Fetch earnings
  const fetchEarnings = async () => {
    if (!currentUser) return;
    try {
      const earningsRef = collection(db, collections.EARNINGS);
      const q = query(earningsRef, where('artistId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const earningsData = querySnapshot.docs[0].data();
        setEarnings({
          total: earningsData.total || 0,
          available: earningsData.available || 0,
          pending: earningsData.pending || 0
        });
        return earningsData;
      } else {
        setEarnings({
          total: 0,
          available: 0,
          pending: 0
        });
        return {
          total: 0,
          available: 0,
          pending: 0
        };
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('Failed to load your earnings');
      return {
        total: 0,
        available: 0,
        pending: 0
      };
    }
  };
  
  // Fetch withdrawals
  const fetchWithdrawals = async () => {
    if (!currentUser) return;
    try {
      const withdrawalsRef = collection(db, collections.WITHDRAWALS);
      const q = query(withdrawalsRef, where('artistId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const withdrawalsData = [];
      querySnapshot.forEach((doc) => {
        withdrawalsData.push({
          $id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort by date (newest first)
      withdrawalsData.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      
      setWithdrawals(withdrawalsData);
      return withdrawalsData;
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to load your withdrawal history');
      return [];
    }
  };
  
  // Handle product deletion
  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      setDeleting(true);
      try {
        await deleteDoc(doc(db, collections.PRODUCTS, product.$id));
        await fetchProducts();
        toast.success(`${product.name} has been deleted successfully`);
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      } finally {
        setDeleting(false);
      }
    }
  };
  
  // Handle event deletion
  const handleDeleteEvent = async (event) => {
    if (window.confirm(`Are you sure you want to delete "${event.title}"?`)) {
      setDeleting(true);
      try {
        await deleteDoc(doc(db, collections.EVENTS, event.$id));
        await fetchEvents();
        toast.success(`${event.title} has been deleted successfully`);
      } catch (error) {
        console.error('Error deleting event:', error);
        toast.error('Failed to delete event');
      } finally {
        setDeleting(false);
      }
    }
  };
  
  // Render dashboard content
  const renderDashboardContent = () => {
    return (
      <>
        {/* Withdraw Banner */}
        {earnings.available > 0 && (
          <WithdrawBanner
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>Available for Withdrawal</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)' }}>
                You have ${earnings.available.toFixed(2)} available to withdraw to your account
              </p>
            </div>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/marketplace/withdraw')}
              style={{ 
                background: 'linear-gradient(90deg, #4a90e2, #63b3ed)',
                boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Withdraw Funds
            </Button>
          </WithdrawBanner>
        )}
        
        {/* Stats Section */}
        <Stats
          as={motion.div}
          variants={itemVariants}
        >
          <h2 style={{ margin: '0 0 1.5rem 0' }}>Earnings Overview</h2>
          <StatsGrid>
            <StatCard variants={itemVariants}>
              <StatTitle>Total Earnings</StatTitle>
              <StatValue>${earnings.total.toFixed(2)}</StatValue>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                Lifetime earnings
              </div>
            </StatCard>
            
            <StatCard variants={itemVariants}>
              <StatTitle>Available Balance</StatTitle>
              <StatValue>${earnings.available.toFixed(2)}</StatValue>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                Ready to withdraw
              </div>
            </StatCard>
            
            <StatCard variants={itemVariants}>
              <StatTitle>Pending Balance</StatTitle>
              <StatValue>${earnings.pending.toFixed(2)}</StatValue>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                Processing payments
              </div>
            </StatCard>
            
            <StatCard variants={itemVariants}>
              <StatTitle>Products Sold</StatTitle>
              <StatValue>{products.reduce((total, product) => total + (product.downloads || 0), 0)}</StatValue>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                Total downloads
              </div>
            </StatCard>
          </StatsGrid>
        </Stats>
        
        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <ContentArea
            as={motion.div}
            variants={itemVariants}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <h2 style={{ margin: 0, color: 'white' }}>Withdrawal History</h2>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                color: 'white',
                minWidth: '600px'
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'left'
                  }}>
                    <th style={{ padding: '1rem 0.5rem' }}>Date</th>
                    <th style={{ padding: '1rem 0.5rem' }}>Amount</th>
                    <th style={{ padding: '1rem 0.5rem' }}>Method</th>
                    <th style={{ padding: '1rem 0.5rem' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((withdrawal, index) => (
                    <tr 
                      key={withdrawal.$id || index}
                      style={{ 
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      <td style={{ padding: '1rem 0.5rem' }}>
                        {new Date(withdrawal.date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem 0.5rem' }}>
                        ${withdrawal.amount.toFixed(2)}
                      </td>
                      <td style={{ padding: '1rem 0.5rem' }}>
                        {withdrawal.method || 'Bank Transfer'}
                      </td>
                      <td style={{ padding: '1rem 0.5rem' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          backgroundColor: 
                            withdrawal.status === 'completed' ? 'rgba(76, 175, 80, 0.2)' :
                            withdrawal.status === 'pending' ? 'rgba(255, 152, 0, 0.2)' :
                            'rgba(244, 67, 54, 0.2)',
                          color: 
                            withdrawal.status === 'completed' ? '#4caf50' :
                            withdrawal.status === 'pending' ? '#ff9800' :
                            '#f44336',
                          border: 
                            withdrawal.status === 'completed' ? '1px solid rgba(76, 175, 80, 0.3)' :
                            withdrawal.status === 'pending' ? '1px solid rgba(255, 152, 0, 0.3)' :
                            '1px solid rgba(244, 67, 54, 0.3)',
                        }}>
                          {withdrawal.status || 'completed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ContentArea>
        )}
        
        {/* Quick Actions */}
        <ContentArea
          as={motion.div}
          variants={itemVariants}
        >
          <h2 style={{ margin: '0 0 1.5rem 0' }}>Quick Actions</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '1rem' 
          }}>
            <Button 
              variant="outlined" 
              startIcon={<FaPlus />}
              onClick={() => navigate('/marketplace/add-product')}
              style={{ 
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '0.75rem 1rem',
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Add New Product
            </Button>
            
            <Button 
              variant="outlined" 
              startIcon={<FaPlus />}
              onClick={() => navigate('/marketplace/add-event')}
              style={{ 
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '0.75rem 1rem',
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Create New Event
            </Button>
          </div>
        </ContentArea>
      </>
    );
  };
  
  // Loading state
  if (loading && !deleting) {
    return (
      <Container>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column'
        }}>
          <FaSpinner style={{ 
            fontSize: '3rem', 
            color: '#4a90e2',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }} />
          <h2>Loading your dashboard...</h2>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </Container>
    );
  }
  
  return (
    <Container
      as={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Header
        as={motion.div}
        variants={itemVariants}
      >
        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      </Header>
      
      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'dashboard' && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderDashboardContent()}
          </motion.div>
        )}
        
        {activeTab === 'products' && (
          <motion.div
            key="products"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ProductsTab 
              products={products} 
              loading={loading} 
              navigate={navigate} 
              handleDeleteProduct={handleDeleteProduct} 
            />
          </motion.div>
        )}
        
        {activeTab === 'events' && (
          <motion.div
            key="events"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <EventsTab 
              events={events} 
              loading={loading} 
              navigate={navigate} 
              handleDeleteEvent={handleDeleteEvent} 
            />
          </motion.div>
        )}
        
        {activeTab === 'analytics' && (
          <motion.div
            key="analytics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AnalyticsTab 
              earnings={earnings} 
              products={products} 
              events={events} 
              loading={loading} 
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default ArtistDashboardNew;
